import { reportError } from "@dash0/sdk-web"
import styled from "@emotion/styled"
import { PButton, PHeading, PText } from "@porsche-design-system/components-react"
import { spacingFluidSm } from "@porsche-design-system/components-react/emotion"
import type { ErrorInfo, PropsWithChildren, ReactNode } from "react"
import { Component } from "react"

interface GlobalErrorBoundaryProps extends PropsWithChildren {
	fallback?: ReactNode
}

interface GlobalErrorBoundaryState {
	error: Error | null
	hasError: boolean
}

interface UserAgentData {
	brands?: { brand: string; version: string }[]
	mobile?: boolean
	platform?: string
}

export class GlobalErrorBoundary extends Component<GlobalErrorBoundaryProps, GlobalErrorBoundaryState> {
	constructor(props: GlobalErrorBoundaryProps) {
		super(props)
		this.state = { error: null, hasError: false }
	}

	static getDerivedStateFromError(error: Error): GlobalErrorBoundaryState {
		return { error, hasError: true }
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		const userAgentData = (navigator as { userAgentData?: UserAgentData }).userAgentData
		reportError(error, {
			attributes: userAgentData
				? {
						brands: {
							arrayValue: {
								values: (userAgentData.brands ?? []).map((brand) => ({
									stringValue: `${brand.brand} ${brand.version}`,
								})),
							},
						},
						mobile: { boolValue: userAgentData.mobile },
						platform: { stringValue: userAgentData.platform },
					}
				: undefined,
			componentStack: errorInfo.componentStack,
		})
	}

	render() {
		const { hasError } = this.state
		const { children, fallback } = this.props

		if (!hasError) {
			return children
		}

		if (fallback) {
			return fallback
		}

		return (
			<Center>
				<PHeading size="xl">An unexpected error occurred</PHeading>
				<PText>
					We have already been notified about this problem. In most cases, the issue is resolved shortly. If
					the error persists, please contact Support.
				</PText>
				<StyledButton type="button" onClick={() => window.location.reload()}>
					Reload Page
				</StyledButton>
			</Center>
		)
	}
}

const Center = styled.div`
	margin: 0 auto;
	display: flex;
	gap: ${spacingFluidSm};
	flex-direction: column;
	justify-content: center;
	min-height: 80vh;
	max-width: 600px;
	padding: 2rem;
`

const StyledButton = styled(PButton)`
	margin-top: ${spacingFluidSm};
	align-self: start;
`
